import {
    ArrowBack, CloudUpload, Delete, Edit, Image as ImageIcon, VideoLibrary, Visibility,
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import VideoModal from '../common/components/VideoModal';
// Ajusta el import de arriba y el del user si tu app los expone distinto.

const emptyForm = {
    id: null,
    title: '',
    help: true,
    viewOn: '',
    video: null,
    thumbnail: null,
};

const chip_labels = {
    mobile: 'Movil',
    desktop: 'Escritorio',
    '': 'Ambos',
    both: 'Ambos'
}

const HelpVideosAdminPage = () => {
    const navigate = useNavigate();

    const user = useSelector((state) => state.session.user);

    const [videos, setVideos] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [notification, setNotification] = useState('');
    const [preview, setPreview] = useState(null); // { url, title }
    const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
    const [thumbPreviewUrl, setThumbPreviewUrl] = useState(null);

    const loadVideos = () => {
        fetch('/help-videos', { credentials: 'include' })
            .then((res) => res.json())
            .then(setVideos)
            .catch(() => setNotification('No se pudo cargar la lista de videos'));
    };

    useEffect(() => {
        loadVideos();
    }, []);

    // Preview local del video recién seleccionado (antes de subirlo)
    useEffect(() => {
        if (!form.video) {
            setVideoPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(form.video);
        setVideoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [form.video]);

    // Preview local del thumbnail recién seleccionado
    useEffect(() => {
        if (!form.thumbnail) {
            setThumbPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(form.thumbnail);
        setThumbPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [form.thumbnail]);

    if (!user?.administrator) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography>No tienes permisos para ver esta página.</Typography>
            </Box>
        );
    }

    const resetForm = () => setForm(emptyForm);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.title || (!form.video && !form.id)) {
            setNotification('Título y video son requeridos');
            return;
        }

        const data = new FormData();
        data.append('title', form.title);
        data.append('help', String(form.help));
        if (form.viewOn) data.append('viewOn', form.viewOn);
        if (form.video) data.append('video', form.video);
        if (form.thumbnail) data.append('thumbnail', form.thumbnail);

        const xhr = new XMLHttpRequest();
        xhr.open(form.id ? 'PUT' : 'POST', `/help-videos${form.id ? `/${form.id}` : ''}`);
        xhr.withCredentials = true;

        xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };

        xhr.onloadstart = () => setUploading(true);
        xhr.onloadend = () => {
            setUploading(false);
            setProgress(0);
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                setNotification(form.id ? 'Video actualizado' : 'Video agregado');
                resetForm();
                loadVideos();
            } else {
                setNotification('Error al guardar el video');
            }
        };
        xhr.onerror = () => setNotification('Error al guardar el video');

        xhr.send(data);
    };

    const handleEdit = (video) => {
        setForm({
            id: video.id,
            title: video.title,
            help: video.help ?? true,
            viewOn: video.viewOn || '',
            video: null,
            thumbnail: null,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = (id) => {
        if (!window.confirm('¿Eliminar este video de la lista de ayuda?')) return;
        fetch(`/help-videos/${id}`, { method: 'DELETE', credentials: 'include' })
            .then(() => {
                setNotification('Video eliminado');
                loadVideos();
            })
            .catch(() => setNotification('Error al eliminar'));
    };

    return (
        <>
            <AppBar position="fixed" elevation={0}>
                <Toolbar>
                    <IconButton color="inherit" edge="start" sx={{ mr: 2 }} onClick={() => navigate('/')}>
                        <ArrowBack style={{ color: 'white' }} />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Videos de ayuda
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box sx={{ bgcolor: '#f4f5f7', minHeight: '100vh', pt: '88px', pb: 6 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 } }}>
                    <Grid container spacing={3}>
                        {/* ---- Formulario ---- */}
                        <Grid item xs={12} md={4}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    border: '1px solid #e3e5e8',
                                    position: 'sticky',
                                    top: 96,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {form.id ? 'Editar video' : 'Agregar video'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {form.id
                                        ? 'Actualiza los datos o reemplaza los archivos.'
                                        : 'Sube el video y su tutorial quedará disponible al instante.'}
                                </Typography>

                                <Box
                                    component="form"
                                    onSubmit={handleSubmit}
                                    sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                                >
                                    <TextField
                                        label="Título"
                                        size="small"
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        required
                                    />

                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Ver en</InputLabel>
                                        <Select
                                            value={form.viewOn}
                                            label="Ver en"
                                            onChange={(e) => setForm({ ...form, viewOn: e.target.value })}
                                        >
                                            <MenuItem value="">Ambos</MenuItem>
                                            <MenuItem value="desktop">Solo escritorio</MenuItem>
                                            <MenuItem value="mobile">Solo movil</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={form.help}
                                                onChange={(e) => setForm({ ...form, help: e.target.checked })}
                                            />
                                        )}
                                        label="Mostrar en la sección de ayuda"
                                    />

                                    {/* Video: selector + preview */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            startIcon={<VideoLibrary />}
                                            sx={{ flex: 1, justifyContent: 'flex-start', textTransform: 'none', minWidth: 0 }}
                                        >
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {form.video ? form.video.name : 'Seleccionar video'}
                                            </Box>
                                            <input
                                                type="file"
                                                accept="video/*"
                                                hidden
                                                onChange={(e) => setForm({ ...form, video: e.target.files[0] })}
                                            />
                                        </Button>
                                        {videoPreviewUrl && (
                                            <IconButton
                                                onClick={() => setPreview({ url: videoPreviewUrl, title: form.title || 'Vista previa' })}
                                                sx={{ border: '1px solid #e3e5e8', borderRadius: 1.5 }}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>

                                    {/* Thumbnail: selector + preview */}
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            startIcon={<ImageIcon />}
                                            sx={{ flex: 1, justifyContent: 'flex-start', textTransform: 'none', minWidth: 0 }}
                                        >
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {form.thumbnail ? form.thumbnail.name : 'Thumbnail (opcional)'}
                                            </Box>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                hidden
                                                onChange={(e) => setForm({ ...form, thumbnail: e.target.files[0] })}
                                            />
                                        </Button>
                                        {thumbPreviewUrl && (
                                            <Box
                                                component="img"
                                                src={thumbPreviewUrl}
                                                alt="Preview del thumbnail"
                                                sx={{
                                                    width: 44, height: 44, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #e3e5e8',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    {!form.thumbnail && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                                            Si no subes uno, se genera automático desde el video.
                                        </Typography>
                                    )}

                                    {uploading && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, borderRadius: 1, height: 6 }} />
                                            <Typography variant="caption" color="text.secondary">{progress}%</Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disableElevation
                                            disabled={uploading}
                                            startIcon={<CloudUpload />}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            {form.id ? 'Guardar cambios' : 'Subir video'}
                                        </Button>
                                        {form.id && (
                                            <Button onClick={resetForm} disabled={uploading} sx={{ textTransform: 'none' }}>
                                                Cancelar
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* ---- Lista de videos existentes ---- */}
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Videos existentes
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {videos.length} en total
                                </Typography>
                            </Box>

                            {videos.length === 0 ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed #d0d3d8', color: 'text.secondary',
                                    }}
                                >
                                    Todavía no hay videos. Agrega el primero desde el formulario.
                                </Paper>
                            ) : (
                                <Grid container spacing={2}>
                                    {videos.map((video) => (
                                        <Grid item xs={12} sm={6} key={video.id}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    borderRadius: 3,
                                                    border: '1px solid #e3e5e8',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    transition: 'box-shadow .15s ease',
                                                    '&:hover': { boxShadow: '0 4px 16px rgba(20,20,30,0.08)' },
                                                }}
                                            >
                                                <Box sx={{ position: 'relative' }}>
                                                    <CardMedia
                                                        component="img"
                                                        image={video.thumbnailUrl}
                                                        alt={`Thumbnail de ${video.title}`}
                                                        sx={{ height: 140, objectFit: 'cover', bgcolor: '#eceef1' }}
                                                    />
                                                    <IconButton
                                                        onClick={() => setPreview({ url: video.videoUrl, title: video.title })}
                                                        sx={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            m: 'auto',
                                                            width: 44,
                                                            height: 44,
                                                            bgcolor: 'rgba(0,0,0,0.55)',
                                                            color: '#fff',
                                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                                        }}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                        {video.title}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            size="small"
                                                            label={video.help === false ? 'Oculto' : 'Visible'}
                                                            color={video.help === false ? 'default' : 'success'}
                                                            variant={video.help === false ? 'outlined' : 'filled'}
                                                        />
                                                        <Chip size="small" label={video.viewOn ? chip_labels[video.viewOn] : chip_labels['both']} variant="outlined" />
                                                    </Box>
                                                </CardContent>
                                                <CardActions sx={{ px: 2, pb: 1.5, pt: 0, justifyContent: 'flex-end' }}>
                                                    <IconButton size="small" onClick={() => handleEdit(video)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDelete(video.id)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {preview && (
                <VideoModal
                    videoUrl={preview.url}
                    onClose={() => setPreview(null)}
                />
            )}

            <Snackbar
                open={!!notification}
                message={notification}
                autoHideDuration={4000}
                onClose={() => setNotification('')}
            />
        </>
    );
};

export default HelpVideosAdminPage;